import { useState, useEffect, useMemo } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { wideTrips } from "@/lib/tripsFetch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ClipboardCheck, AlertTriangle, ArrowRightLeft, X } from "lucide-react";
import { dayKey, fmtDayKey, fmtTime, fmtDateTime } from "@/lib/time";

export const startOf = (t) => t.pickup_timestamp || t.scheduled_pickup;

// Build one row per day per driver from trips, inspections and incidents on this vehicle.
export function buildDays(trips, inspections, incidents) {
  const rows = {};
  const row = (day, driverId, driverName) => {
    const k = `${day}|${driverId || "?"}`;
    rows[k] = rows[k] || { day, driverId, driverName, trips: [], inspections: [], incidents: [] };
    if (!rows[k].driverName && driverName) rows[k].driverName = driverName;
    return rows[k];
  };
  trips.forEach((t) => { const s = startOf(t); if (s) row(dayKey(s), t.assigned_driver_id, t.assigned_driver_name).trips.push(t); });
  inspections.forEach((i) => row(i.date || dayKey(i.created_at), i.driver_id, i.driver_name).inspections.push(i));
  incidents.forEach((i) => row(dayKey(i.reported_at || i.created_at), i.driver_id, i.driver_name).incidents.push(i));
  return Object.values(rows).sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
}

export default function VehicleHistoryDialog({ vehicle, drivers, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [date, setDate] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (!open || !vehicle) { return; }
    setData(null); setDate("");
    Promise.all([
      wideTrips(user?.role === "owner").then((data) => ({ data })),
      fleetApi.get("/inspections").catch(() => ({ data: [] })),
      fleetApi.get("/incidents").catch(() => ({ data: [] })),
      api.get(`/vehicle-log/${vehicle.id}`).catch(() => ({ data: [] })),
    ]).then(([t, i, inc, l]) => {
      const trips = (t.data || []).filter((x) => x.assigned_vehicle_id === vehicle.id);
      const tripIds = new Set(trips.map((x) => x.id));
      setData({
        trips,
        inspections: (Array.isArray(i.data) ? i.data : []).filter((x) => x.vehicle_id === vehicle.id),
        incidents: (Array.isArray(inc.data) ? inc.data : []).filter((x) => x.vehicle_id === vehicle.id || (x.trip_id && tripIds.has(x.trip_id))),
        log: l.data || [],
      });
    }).catch((e) => toast.error(fleetError(e, "Couldn't load vehicle history.")));
  }, [open, vehicle, user?.role]);

  const days = useMemo(() => (data ? buildDays(data.trips, data.inspections, data.incidents) : []), [data]);
  const name = (id, fallback) => drivers.find((d) => d.id === id)?.full_name || fallback || "Unknown driver";
  const shown = date ? days.filter((d) => d.day === date) : days.slice(0, 60);
  const holderOn = date && data ? data.log.find((e) => dayKey(e.at) <= date) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white sm:max-w-3xl" data-testid="vehicle-history-dialog">
        <DialogHeader>
          <DialogTitle data-testid="vehicle-history-title">History · {vehicle?.fleet_number} {vehicle?.year} {vehicle?.make} {vehicle?.model}</DialogTitle>
          <DialogDescription className="text-slate-400">Who drove this vehicle and when, from trips, inspections, incidents and hand-overs.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-10" data-testid="vehicle-history-loading"><Loader2 className="h-6 w-6 animate-spin text-blue-400" /></div>
        ) : (
          <div className="space-y-6">
            <DateLookup date={date} setDate={setDate} />
            {date && <LookupResult date={date} rows={shown} holder={holderOn} name={name} />}
            <HandOvers log={data.log} />
            <section data-testid="vehicle-history-days">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Daily use{date ? ` · ${fmtDayKey(date)}` : " · latest 60"}</h3>
              {shown.length === 0 ? <p className="text-sm text-slate-500" data-testid="vehicle-history-empty">No trips, inspections or incidents recorded{date ? " on this day" : ""}.</p> : (
                <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800">
                  {shown.map((r) => <DayRow key={`${r.day}-${r.driverId}`} r={r} name={name} />)}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DateLookup = ({ date, setDate }) => (
  <div className="flex flex-wrap items-end gap-3 rounded-lg border border-blue-600/30 bg-blue-600/5 p-4">
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-blue-300">Who had it on…</label>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="vehicle-history-date"
        className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-blue-600" />
    </div>
    {date && <button onClick={() => setDate("")} data-testid="vehicle-history-date-clear" className="inline-flex items-center gap-1 rounded-md px-2 py-2 text-xs text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-3.5 w-3.5" /> Show all</button>}
    <p className="text-xs text-slate-500">Use the date on a ticket or damage report.</p>
  </div>
);

const LookupResult = ({ date, rows, holder, name }) => {
  const used = rows.filter((r) => r.trips.length || r.inspections.length);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 text-sm" data-testid="vehicle-history-lookup-result">
      {used.length ? (
        <p className="text-white">On {fmtDayKey(date)} this vehicle was driven by <span className="font-semibold text-emerald-300">{used.map((r) => name(r.driverId, r.driverName)).join(" and ")}</span>.</p>
      ) : <p className="text-slate-300">No trips or inspections were recorded on this vehicle on {fmtDayKey(date)}.</p>}
      {holder && <p className="mt-1 text-xs text-slate-400">Assigned by the end of that day (latest hand-over): {holder.driver_id ? name(holder.driver_id, holder.driver_name) : "nobody"}.</p>}
    </div>
  );
};

const HandOvers = ({ log }) => (
  <section data-testid="vehicle-history-handovers">
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Hand-overs</h3>
    {log.length === 0 ? <p className="text-sm text-slate-500">No hand-overs recorded yet. Changes made with Assign / Change are logged from now on.</p> : (
      <ul className="space-y-1.5">
        {log.map((e, i) => (
          <li key={e.at + i} className="flex items-start gap-2 text-sm text-slate-300" data-testid={`vehicle-handover-${i}`}>
            <ArrowRightLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
            <span><span className="font-mono-data text-xs text-slate-500">{fmtDateTime(e.at)}</span> · {e.from_driver_name || "Free"} → <span className="text-white">{e.driver_id ? e.driver_name || e.driver_id : "Free"}</span> <span className="text-slate-500">by {e.by_name}</span></span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const DayRow = ({ r, name }) => {
  const times = r.trips.map(startOf).filter(Boolean).sort();
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid={`vehicle-history-row-${r.day}-${r.driverId || "unknown"}`}>
      <div>
        <span className="font-mono-data text-xs text-slate-500">{fmtDayKey(r.day)}</span>
        <span className="ml-3 font-medium text-white">{name(r.driverId, r.driverName)}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-400">
        {r.trips.length > 0 && <span>{r.trips.length} trip{r.trips.length > 1 ? "s" : ""} · {fmtTime(times[0])}{times.length > 1 ? `–${fmtTime(times[times.length - 1])}` : ""}</span>}
        {r.inspections.length > 0 && <span className="inline-flex items-center gap-1 text-emerald-300"><ClipboardCheck className="h-3.5 w-3.5" />Inspection</span>}
        {r.incidents.length > 0 && <span className="inline-flex items-center gap-1 text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />{r.incidents.length} incident{r.incidents.length > 1 ? "s" : ""}</span>}
      </div>
    </li>
  );
};
